import { TableRow, TableCell, Stack, IconButton, Typography, Divider, Box, Button } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { InvoiceRowDto, UpdateInvoiceRowDto } from '@sr/dto';
import { DatePickerField } from 'shared/ui/formik-fields/date-picker';
import { MediaPickerField } from 'entities/media-picker';
import { VatPickerField } from 'shared/ui/formik-fields/vat-picker';
import { LinkToMedia } from 'shared/ui/links/link-to-media';
import PricePair from 'shared/ui/price-pair';
import PriceSpan from 'shared/ui/price-span';
import { Formik, Field } from 'formik';
import { TextField } from 'formik-mui';
import { useState } from 'react';
import { formatDate, formatDatePeriod } from 'utils/date-format-helpers';
import { FormData, toDto, fromDto, validate } from './invoice-row.form';
import { usePlacementCell } from './placement-cell';
import { purchaseLinkManager } from 'utils/link-manager';
import { LinkToPurchase } from 'shared/ui/links/link-to-purchase';
import { ButtonLink } from 'shared/ui/link-base';

type RowProps = {
  row: InvoiceRowDto;
  isNewRow?: boolean;
  isReadOnly?: boolean;
  isClientInvoice: boolean;
  sellerVat: number | null | undefined;
  onPlaceClick?: (row: InvoiceRowDto) => void;
  onSaveClick: (data: UpdateInvoiceRowDto) => Promise<void>;
  onCopyClick?: (row: InvoiceRowDto) => Promise<void>;
  onCancelClick?: (row: InvoiceRowDto) => void;
  onDeleteClick?: (row: InvoiceRowDto) => Promise<void>;
  isCompact?: boolean;
  index?: number;
};

const priceTextStyle = { color: '#ed6c02', fontWeight: 700 };

const PriceWithoutVAT = ({ priceExclVat }: { priceExclVat: number }) => {
  return (
    <div>
      <Typography variant="caption" sx={priceTextStyle} component="div">
        Без НДС
      </Typography>
      <PriceSpan size="small" price={priceExclVat} />
      <Divider sx={{ my: 1 }} />
      <Typography variant="caption" sx={priceTextStyle} component="div">
        С НДС
      </Typography>
    </div>
  );
};

export const InvoiceRow = ({
  isNewRow,
  isReadOnly = false,
  row,
  sellerVat,
  isClientInvoice,
  onPlaceClick,
  onSaveClick,
  onCopyClick,
  onDeleteClick,
  onCancelClick,
  isCompact = false,
  index
}: RowProps) => {
  const { media, description, startDate, endDate, amount, price, priceExclVat, priceDiscount, vat } = row;

  const [editMode, setEditMode] = useState(false);

  const onSubmitClick = async (values: FormData) => {
    setEditMode(false);
    return onSaveClick(toDto(row, values));
  };

  const toggleEdit = () => {
    setEditMode(!editMode);
  };
  const period = `${formatDate(startDate)} - ${formatDate(endDate)} (${formatDatePeriod(new Date(startDate), new Date(endDate))})`;
  const displayEdit = editMode || isNewRow;

  const { disableDelete } = usePlacementCell(!!isNewRow, row, isClientInvoice);

  return (
    <TableRow
    // sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
    >
      <TableCell sx={{width: '1%'}}><Typography fontSize={'1.1rem'}>{index}</Typography></TableCell>
      <Formik initialValues={fromDto(row)} onSubmit={onSubmitClick} validate={validate} enableReinitialize>
        {({ submitForm, resetForm, dirty, isValid }) => (
          <>
            <TableCell width={600}>
              <Stack direction="column" gap={1}>
                {!displayEdit ? media ? <LinkToMedia media={media}>{media.name}</LinkToMedia> : '-' : <Field name="mediaId" component={MediaPickerField} size="small" />}
                {!displayEdit ? (
                  description.length > 0 && (
                    <Stack>
                      {isClientInvoice && (
                        <Typography variant="caption" fontWeight={600}>
                          Описание
                        </Typography>
                      )}
                      <Typography>{description}</Typography>
                    </Stack>
                  )
                ) : (
                  <Field name="description" component={TextField} size="small" multiline fullWidth label="Описание" />
                )}
              </Stack>
            </TableCell>
            <TableCell width={200}>
              {!displayEdit ? (
                period
              ) : (
                <>
                  <Stack
                    direction="column"
                    spacing={1}
                    sx={{
                      '& .MuiInputBase-root': {
                        maxWidth: '138px',
                        '& input': { maxWidth: '80px' },
                        '& button': { padding: 0 },
                      },
                    }}
                  >
                    <Field name="startDate" label="" component={DatePickerField} size="small" />
                    <Field name="endDate" label="" component={DatePickerField} size="small" />
                  </Stack>
                </>
              )}
            </TableCell>

            <TableCell width={50}>{!displayEdit ? `${amount} шт.` : <Field name="amount" label="" component={TextField} size="small" />}</TableCell>

            <TableCell width={130}>{amount && <PriceSpan size="small" price={priceExclVat / amount} />}</TableCell>

            <TableCell width={130}>
              {!displayEdit ? (
                <Box>
                  <PriceWithoutVAT priceExclVat={priceExclVat} />
                  <PricePair size="small" price={price} priceDiscount={priceDiscount} />
                </Box>
              ) : (
                <>
                  <PriceWithoutVAT priceExclVat={priceExclVat} />
                  <Stack direction="column" spacing={1} mt={1}>
                    <Field name="price" label="без скидки" component={TextField} size="small" />
                    <Field name="priceDiscount" label="со скидкой" component={TextField} size="small" />
                  </Stack>
                </>
              )}
            </TableCell>

            <TableCell width={100}>{row.vat === null ? 'без НДС' : `${row.vat}%`}</TableCell>

            <TableCell>
              {!isNewRow && !isCompact && isClientInvoice && (
                row.linkedPurchase ? (
                  <ButtonLink
                    to={purchaseLinkManager.link(row.linkedPurchase.id)}
                    buttonProps={{ size: 'small', variant: 'text', sx: { whiteSpace: 'nowrap' } }}
                  >
                    {`Заявка № ${row.linkedPurchase.id}`}
                  </ButtonLink>
                ) : (
                  <Button size="small" variant="outlined" onClick={() => onPlaceClick?.(row)} sx={{ whiteSpace: 'nowrap' }}>
                    Разместить
                  </Button>
                )
              )}

              {!isNewRow && !isCompact && !isClientInvoice && (
                <>
                  <Button size="small" variant={row.linkedPurchase ? 'outlined' : 'contained'} onClick={() => onPlaceClick?.(row)} sx={{ whiteSpace: 'nowrap' }}>
                    {row.linkedPurchase ? 'Отвязать' : 'Привязать'}
                  </Button>
                  {row.linkedPurchase ? (
                    <>
                      <br />
                      <br />
                      <LinkToPurchase purchase={row.linkedPurchase}>Заявка №{row.linkedPurchase?.id}</LinkToPurchase>
                    </>
                  ) : (
                    <></>
                  )}
                </>
              )}
            </TableCell>

            <TableCell align="right" width={60}>
              <Stack sx={{ display: 'flex', flexDirection: 'column' }}>
                {!displayEdit ? (
                  <>
                    <IconButton edge="end" onClick={toggleEdit} disabled={isReadOnly}>
                      <EditIcon />
                    </IconButton>
                    {!isNewRow && (
                      <IconButton edge="end" onClick={() => onCopyClick?.(row)} disabled={isReadOnly}>
                        <ContentCopyIcon />
                      </IconButton>
                    )}
                  </>
                ) : (
                  <>
                    <IconButton edge="end" onClick={submitForm} disabled={!dirty || !isValid} type="submit">
                      <CheckIcon />
                    </IconButton>
                    <IconButton
                      edge="end"
                      onClick={() => {
                        resetForm();
                        toggleEdit();
                        onCancelClick?.(row);
                      }}
                    >
                      <CancelIcon />
                    </IconButton>
                  </>
                )}
              </Stack>
              {!isNewRow && (
                <IconButton edge="end" disabled={isReadOnly || disableDelete} onClick={() => onDeleteClick?.(row)}>
                  <DeleteIcon />
                </IconButton>
              )}
            </TableCell>
          </>
        )}
      </Formik>
    </TableRow>
  );
};
